/**
 * Cryoport location template auto-save: detect when origin/destination form
 * data is not an exact match for any existing location template and save it.
 * A single location template pool is shared between origin and destination.
 */

export interface CryoLocationTemplateRowForAutosave {
  id: string;
  location_label: string;
  cryo_name?: string | null;
  cryo_site_template_id?: number | null;
  cryo_location_id?: number | string | null;
  cryo_street_address_line_one?: string | null;
  cryo_street_address_line_two?: string | null;
  cryo_city?: string | null;
  cryo_state?: string | null;
  cryo_zip_code?: string | null;
  cryo_country_code?: string | null;
  cryo_time_zone?: string | null;
  cryo_residential?: boolean;
  cryo_contact_id?: number | string | null;
  cryo_contact_first_name?: string | null;
  cryo_contact_last_name?: string | null;
  cryo_contact_email?: string | null;
  cryo_contact_phone_number?: string | null;
  cryo_site_process?: string | null;
}

/** Subset of BookShipmentFormValues used for autosave comparison. */
export interface CryoLocationAutosaveFormValues {
  cryo_origin_name?: string | null;
  cryo_origin_site_template_id?: number | null;
  cryo_origin_location_id?: number | string | null;
  cryo_origin_street_address_line_one?: string | null;
  cryo_origin_street_address_line_two?: string | null;
  cryo_origin_city?: string | null;
  cryo_origin_state?: string | null;
  cryo_origin_zip_code?: string | null;
  cryo_origin_country_code?: string | null;
  cryo_origin_time_zone?: string | null;
  cryo_origin_residential?: boolean;
  cryo_origin_contact_id?: number | string | null;
  cryo_origin_contact_first_name?: string | null;
  cryo_origin_contact_last_name?: string | null;
  cryo_origin_contact_email?: string | null;
  cryo_origin_contact_phone_number?: string | null;
  cryo_origin_site_process?: string | null;

  cryo_dest_name?: string | null;
  cryo_dest_site_template_id?: number | null;
  cryo_dest_location_id?: number | string | null;
  cryo_dest_street_address_line_one?: string | null;
  cryo_dest_street_address_line_two?: string | null;
  cryo_dest_city?: string | null;
  cryo_dest_state?: string | null;
  cryo_dest_zip_code?: string | null;
  cryo_dest_country_code?: string | null;
  cryo_dest_time_zone?: string | null;
  cryo_dest_residential?: boolean;
  cryo_dest_contact_id?: number | string | null;
  cryo_dest_contact_first_name?: string | null;
  cryo_dest_contact_last_name?: string | null;
  cryo_dest_contact_email?: string | null;
  cryo_dest_contact_phone_number?: string | null;
  cryo_dest_site_process?: string | null;
}

function normField(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

export function normalizeCryoLocationName(
  s: string | null | undefined
): string {
  return normField(s).toLowerCase();
}

function templateFieldsMatchOrigin(
  t: CryoLocationTemplateRowForAutosave,
  v: CryoLocationAutosaveFormValues
): boolean {
  return (
    normField(t.cryo_street_address_line_one) ===
      normField(v.cryo_origin_street_address_line_one) &&
    normField(t.cryo_street_address_line_two) ===
      normField(v.cryo_origin_street_address_line_two) &&
    normField(t.cryo_city) === normField(v.cryo_origin_city) &&
    normField(t.cryo_state) === normField(v.cryo_origin_state) &&
    normField(t.cryo_zip_code) === normField(v.cryo_origin_zip_code) &&
    normField(t.cryo_country_code) === normField(v.cryo_origin_country_code) &&
    normField(t.cryo_contact_first_name) ===
      normField(v.cryo_origin_contact_first_name) &&
    normField(t.cryo_contact_last_name) ===
      normField(v.cryo_origin_contact_last_name) &&
    normField(t.cryo_contact_email) ===
      normField(v.cryo_origin_contact_email) &&
    normField(t.cryo_contact_phone_number) ===
      normField(v.cryo_origin_contact_phone_number) &&
    normField(t.cryo_time_zone) === normField(v.cryo_origin_time_zone) &&
    Boolean(t.cryo_residential) === Boolean(v.cryo_origin_residential)
  );
}

function templateFieldsMatchDest(
  t: CryoLocationTemplateRowForAutosave,
  v: CryoLocationAutosaveFormValues
): boolean {
  return (
    normField(t.cryo_street_address_line_one) ===
      normField(v.cryo_dest_street_address_line_one) &&
    normField(t.cryo_street_address_line_two) ===
      normField(v.cryo_dest_street_address_line_two) &&
    normField(t.cryo_city) === normField(v.cryo_dest_city) &&
    normField(t.cryo_state) === normField(v.cryo_dest_state) &&
    normField(t.cryo_zip_code) === normField(v.cryo_dest_zip_code) &&
    normField(t.cryo_country_code) === normField(v.cryo_dest_country_code) &&
    normField(t.cryo_contact_first_name) ===
      normField(v.cryo_dest_contact_first_name) &&
    normField(t.cryo_contact_last_name) ===
      normField(v.cryo_dest_contact_last_name) &&
    normField(t.cryo_contact_email) === normField(v.cryo_dest_contact_email) &&
    normField(t.cryo_contact_phone_number) ===
      normField(v.cryo_dest_contact_phone_number) &&
    normField(t.cryo_time_zone) === normField(v.cryo_dest_time_zone) &&
    Boolean(t.cryo_residential) === Boolean(v.cryo_dest_residential)
  );
}

function originExactMatch(
  t: CryoLocationTemplateRowForAutosave,
  v: CryoLocationAutosaveFormValues
): boolean {
  const name = normalizeCryoLocationName(v.cryo_origin_name);
  if (!name) return false;
  if (normalizeCryoLocationName(t.cryo_name) !== name) return false;
  return templateFieldsMatchOrigin(t, v);
}

function destExactMatch(
  t: CryoLocationTemplateRowForAutosave,
  v: CryoLocationAutosaveFormValues
): boolean {
  const name = normalizeCryoLocationName(v.cryo_dest_name);
  if (!name) return false;
  if (normalizeCryoLocationName(t.cryo_name) !== name) return false;
  return templateFieldsMatchDest(t, v);
}

export function shouldAutoSaveCryoOriginLocation(
  templates: CryoLocationTemplateRowForAutosave[],
  v: CryoLocationAutosaveFormValues
): boolean {
  if (!normalizeCryoLocationName(v.cryo_origin_name)) return false;
  return !templates.some((t) => originExactMatch(t, v));
}

export function shouldAutoSaveCryoDestLocation(
  templates: CryoLocationTemplateRowForAutosave[],
  v: CryoLocationAutosaveFormValues
): boolean {
  if (!normalizeCryoLocationName(v.cryo_dest_name)) return false;
  return !templates.some((t) => destExactMatch(t, v));
}

export function buildCryoOriginLocationPostBody(
  v: CryoLocationAutosaveFormValues
): Record<string, unknown> {
  return {
    location_label: normField(v.cryo_origin_name) || null,
    cryo_site_template_id: v.cryo_origin_site_template_id ?? null,
    cryo_name: normField(v.cryo_origin_name) || null,
    cryo_location_id: v.cryo_origin_location_id ?? null,
    cryo_street_address_line_one:
      normField(v.cryo_origin_street_address_line_one) || null,
    cryo_street_address_line_two:
      normField(v.cryo_origin_street_address_line_two) || null,
    cryo_city: normField(v.cryo_origin_city) || null,
    cryo_state: normField(v.cryo_origin_state) || null,
    cryo_zip_code: normField(v.cryo_origin_zip_code) || null,
    cryo_country_code: normField(v.cryo_origin_country_code) || null,
    cryo_time_zone: normField(v.cryo_origin_time_zone) || null,
    cryo_residential: Boolean(v.cryo_origin_residential),
    cryo_contact_id: v.cryo_origin_contact_id ?? null,
    cryo_contact_first_name:
      normField(v.cryo_origin_contact_first_name) || null,
    cryo_contact_last_name: normField(v.cryo_origin_contact_last_name) || null,
    cryo_contact_email: normField(v.cryo_origin_contact_email) || null,
    cryo_contact_phone_number:
      normField(v.cryo_origin_contact_phone_number) || null,
    cryo_site_process: normField(v.cryo_origin_site_process) || null,
  };
}

export function buildCryoDestLocationPostBody(
  v: CryoLocationAutosaveFormValues
): Record<string, unknown> {
  return {
    location_label: normField(v.cryo_dest_name) || null,
    cryo_site_template_id: v.cryo_dest_site_template_id ?? null,
    cryo_name: normField(v.cryo_dest_name) || null,
    cryo_location_id: v.cryo_dest_location_id ?? null,
    cryo_street_address_line_one:
      normField(v.cryo_dest_street_address_line_one) || null,
    cryo_street_address_line_two:
      normField(v.cryo_dest_street_address_line_two) || null,
    cryo_city: normField(v.cryo_dest_city) || null,
    cryo_state: normField(v.cryo_dest_state) || null,
    cryo_zip_code: normField(v.cryo_dest_zip_code) || null,
    cryo_country_code: normField(v.cryo_dest_country_code) || null,
    cryo_time_zone: normField(v.cryo_dest_time_zone) || null,
    cryo_residential: Boolean(v.cryo_dest_residential),
    cryo_contact_id: v.cryo_dest_contact_id ?? null,
    cryo_contact_first_name: normField(v.cryo_dest_contact_first_name) || null,
    cryo_contact_last_name: normField(v.cryo_dest_contact_last_name) || null,
    cryo_contact_email: normField(v.cryo_dest_contact_email) || null,
    cryo_contact_phone_number:
      normField(v.cryo_dest_contact_phone_number) || null,
    cryo_site_process: normField(v.cryo_dest_site_process) || null,
  };
}
